import React, { useCallback, useEffect, useState } from 'react';

import RecordBadge from '../common/RecordBadge';
import { fetchItemHistory } from '../../services/history';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { normalizeId } from '../../utils/ids';
import HistoryModal from './HistoryModal';

export const DELETED_ITEM_NOTICE = 'This item is deleted. Its history is kept, and reads here as it stood — the deletion is the newest entry.';
export const RESTORED_ITEM_NOTICE = 'This item was restored after it was deleted. Open it from its collection to change it.';

/**
 * A DELETED item's history — the same list a live item's ⋯ → History… opens
 * (HistoryModal), from the one place a deleted item can still be seen: its
 * deletion. Two doors open it: a Recently deleted row, and the delete (or
 * intake reject) entry in a collection's History tab.
 *
 * `entry` is that deletion as the API described it (a history entry, or a
 * Recently deleted row, which is one): the item's id, its title and card name
 * as they were, and the card it was on. `GET /api/items/{id}/history` answers
 * for a deleted item under the deletion's own read rule — a manager of the
 * collection, or whoever could have edited the item — so nobody reads here
 * who could not have read it while it lived (keepr-api docs/SCHEMA.md §
 * History › Endpoints).
 *
 * Read-only: there is no record to revert an edit onto. Bringing it back is
 * the Restore beside the door that opened this.
 *
 * The item may have been RESTORED since the deletion that opened this (a
 * delete entry stays in the collection's History). The API says which it is
 * now (`X-Item-Deleted`, fetchItemHistory's `deleted`), and the notice
 * follows: "deleted" only of an item that still is. Until the first page
 * answers there is no notice at all, rather than a guess; an API that does
 * not say (a deploy behind) gets the deleted notice, as before.
 */
export default function DeletedItemHistoryModal({ entry, onClose }) {
	const defs = useOptionalCardDefinitions();
	const itemId = normalizeId(entry?.target?.id);
	const cardId = normalizeId(entry?.scope?.card_id);
	const card = (cardId && defs?.cardsById?.[cardId]) || null;
	const kicker = entry?.target?.card || card?.name || 'Item';
	const title = entry?.target?.label || 'Untitled item';

	// undefined until the first page answers; then true | false | null.
	const [deleted, setDeleted] = useState(undefined);
	useEffect(() => { setDeleted(undefined); }, [itemId]);
	const fetchPage = useCallback(params => fetchItemHistory(itemId, params).then((page) => {
		setDeleted(page?.deleted ?? null);
		return page;
	}), [itemId]);
	const notice = deleted === undefined ? null : deleted === false ? RESTORED_ITEM_NOTICE : DELETED_ITEM_NOTICE;

	return (
		<HistoryModal
			open={Boolean(entry && itemId)}
			onClose={onClose}
			kicker={kicker}
			badge={<RecordBadge kind="item" record={card} title={title} collection={normalizeId(entry?.scope?.collection_id) || null} />}
			notice={notice}
			fetchPage={fetchPage}
			canRevert={false}
		/>
	);
}
